import { get } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { registerDestructor } from '@ember/destroyable';
import Component from '@glimmer/component';
import type Owner from '@ember/owner';
import Modal from 'tio-ui/components/modal';
import Table from 'tio-ui/components/table';
import Paginator from 'tio-ui/components/paginator';
import Button from 'tio-ui/components/button';
import CheckboxList from 'tio-ui/components/checkbox-list';
import type { CheckboxListItem } from 'tio-ui/components/checkbox-list';
import type IntlService from 'ember-intl/services/intl';
import type { Store as StoreService } from '@warp-drive/core';
import type { NotificationsService } from 'tio-ui/components/notifications';
import downloadFile from '../../utils/download-file.ts';
import type FeaturesService from '../../services/features.ts';
import {
  fetchEligibilityRecordsRequest,
  exportEligibilityRecords,
} from 'tio-data/requests/eligibility-file-records';

interface RecordDetailsModalSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    jobLogId: string | null;
    category: string;
    title: string;
  };
}

interface RecordRow {
  payrollId: string;
  merged: Record<string, string>;
}

const PER_PAGE = 25;

// Always written to the CSV by the export endpoint, so they are surfaced as a
// read-only note rather than as toggleable options. `payrollId` is the
// dedicated payroll-id column (never a `merged` key); the other three are
// filtered out of the optional list below.
const ALWAYS_INCLUDED = ['payrollId', 'firstName', 'lastName', 'email'];
const EXPORT_EXCLUDED_FROM_OPTIONAL = new Set(['firstName', 'lastName', 'email']);

export default class RecordDetailsModal extends Component<RecordDetailsModalSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare features: FeaturesService;

  @tracked isLoading = false;
  @tracked hasError = false;
  @tracked records: RecordRow[] = [];
  @tracked columns: string[] = [];
  @tracked currentPage = 1;
  @tracked totalItems = 0;
  @tracked showExportPanel = false;
  @tracked selectedExportFields: string[] = [];
  private hasLoaded = false;

  private abortController: AbortController | null = null;

  constructor(owner: Owner, args: RecordDetailsModalSignature['Args']) {
    super(owner, args);
    registerDestructor(this, () => this.abortController?.abort());
  }

  get hasRecords() {
    return this.records.length > 0;
  }

  get alwaysIncludedLabel() {
    return ALWAYS_INCLUDED.join(', ');
  }

  // Optional export fields = the current columns minus the fields that the
  // endpoint always includes. Labels mirror the modal/file headers verbatim
  // (camelCase) so the selected `key` doubles as the export field name.
  get optionalExportFields(): CheckboxListItem[] {
    return this.columns
      .filter((col) => !EXPORT_EXCLUDED_FROM_OPTIONAL.has(col))
      .map((field) => ({ key: field, label: field }));
  }

  private triggerLoad = modifier(() => {
    if (!this.hasLoaded) {
      this.hasLoaded = true;
      this.onOpen();
    }
  });

  loadRecords = async (page: number) => {
    const previousPage = this.currentPage;
    this.isLoading = true;
    this.hasError = false;

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const { content: response } = (await this.store.request(
        fetchEligibilityRecordsRequest(
          this.args.jobLogId || '',
          this.args.category,
          page,
          PER_PAGE,
          this.abortController.signal
        )
      )) as { content: { data?: RecordRow[]; meta?: { total?: number } } };

      const data = response.data || [];
      this.totalItems = response.meta?.total || data.length;

      // Union the merged keys across every record on the page rather than reading
      // only the first record — records (e.g. deactivated rows) can carry differing
      // field sets, so keying off a single row would drop columns other rows have.
      const columnSet = new Set<string>();
      for (const record of data) {
        if (record?.merged) {
          Object.keys(record.merged).forEach((key) => columnSet.add(key));
        }
      }
      if (columnSet.size > 0) {
        this.columns = Array.from(columnSet);
      }

      this.records = data.map(
        (record: { payroll_id?: string; merged?: Record<string, string> }) => ({
          payrollId: record.payroll_id || '',
          merged: record.merged || {},
        })
      );

      this.currentPage = page;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        this.currentPage = previousPage;
        return;
      }
      this.records = [];
      this.totalItems = 0;
      this.currentPage = previousPage;
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  };

  onOpen = async () => {
    this.records = [];
    this.columns = [];
    this.currentPage = 1;
    this.totalItems = 0;
    this.hasError = false;
    this.showExportPanel = false;
    this.selectedExportFields = [];

    await this.loadRecords(1);
  };

  onPageChange = (page: number) => {
    this.loadRecords(page);
  };

  openExportPanel = () => {
    this.selectedExportFields = [];
    this.showExportPanel = true;
  };

  closeExportPanel = () => {
    this.showExportPanel = false;
  };

  setSelectedExportFields = (keys: string[]) => {
    this.selectedExportFields = keys;
  };

  downloadExport = dropTask(async () => {
    const jobLogId = this.args.jobLogId;
    if (!jobLogId) {
      this.notifications.add(this.intl.t('eligibility_files.records.export.error'), {
        appearance: 'error',
      });
      return;
    }

    try {
      // An uninitialised features service would send an empty key and come back as a
      // server error, hiding the real cause.
      const apiKey = this.features.ENV?.apiKey;
      if (!apiKey) {
        throw new Error(
          'Eligibility record export has no API key: features service not initialised'
        );
      }

      const response = await exportEligibilityRecords(
        jobLogId,
        this.args.category,
        this.selectedExportFields,
        { apiKey }
      );

      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const match = contentDisposition?.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
      const filename = match?.[1] ?? `eligibility-${jobLogId}-${this.args.category}.csv`;

      const blob = await response.blob();
      downloadFile(blob, filename, 'text/csv');
      this.showExportPanel = false;
    } catch {
      // Keep the panel open so the user can retry after a transient failure.
      this.notifications.add(this.intl.t('eligibility_files.records.export.error'), {
        appearance: 'error',
      });
    }
  });

  cancelRequest = () => {
    this.abortController?.abort();
    this.isLoading = false;
  };

  handleClose = () => {
    this.cancelRequest();
    this.hasLoaded = false;
    this.showExportPanel = false;
    this.args.onClose();
  };

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{this.handleClose}} @size="90vw" @ariaLabel={{@title}}>
      <:header>
        <span {{this.triggerLoad}}>{{@title}}</span>
      </:header>
      <:body>
        {{#if this.isLoading}}
          <div class="tio-row-between mb-4">
            <span class="tio-fg-muted">{{t "eligibility_files.records.loading"}}</span>
            <Button @appearance="outlined" {{on "click" this.cancelRequest}}>
              {{t "eligibility_files.records.cancel"}}
            </Button>
          </div>
        {{/if}}

        {{#if this.hasError}}
          <div class="p-4 mb-4 tio-fg-danger-body tio-bg-danger-soft rounded" data-test-error>
            <span>{{t "eligibility_files.records.load_error"}}</span>
            <Button
              @appearance="outlined"
              class="ml-4"
              {{on "click" (fn this.loadRecords this.currentPage)}}
            >
              {{t "eligibility_files.records.retry"}}
            </Button>
          </div>
        {{/if}}

        {{#if this.showExportPanel}}
          <div data-test-eligibility-export-panel class="tio-col tio-inset-panel">
            <p data-test-eligibility-export-always-included class="tio-fg-muted">
              {{t "eligibility_files.records.export.always_included"}}
              {{this.alwaysIncludedLabel}}
            </p>

            <div data-test-eligibility-export-fields>
              <CheckboxList
                @label={{t "eligibility_files.records.export.optional_fields"}}
                @items={{this.optionalExportFields}}
                @selectedKeys={{this.selectedExportFields}}
                @onChange={{this.setSelectedExportFields}}
                @emptyText={{t "eligibility_files.records.export.no_optional_fields"}}
              />
            </div>

            <div class="tio-cluster">
              <Button
                data-test-eligibility-download-csv-button
                @intent="primary"
                @isRunning={{this.downloadExport.isRunning}}
                {{on "click" this.downloadExport.perform}}
              >
                {{t "eligibility_files.records.export.download_csv"}}
              </Button>
              <Button @appearance="outlined" {{on "click" this.closeExportPanel}}>
                {{t "eligibility_files.records.cancel"}}
              </Button>
            </div>
          </div>
        {{/if}}

        <Table @isLoading={{this.isLoading}} @previewRowLimit={{5}}>
          <:thead as |head|>
            <head.tr>
              <head.th>{{t "eligibility_files.records.payroll_id"}}</head.th>
              {{#each this.columns as |col|}}
                <head.th>{{col}}</head.th>
              {{/each}}
            </head.tr>
          </:thead>
          <:tbody as |body|>
            {{#each this.records as |record|}}
              <body.tr>
                <body.td>{{record.payrollId}}</body.td>
                {{#each this.columns as |col|}}
                  <body.td>{{get record.merged col}}</body.td>
                {{/each}}
              </body.tr>
            {{else}}
              {{#unless this.isLoading}}
                <body.tr>
                  <body.td>
                    <span class="tio-fg-muted">{{t "eligibility_files.records.no_records"}}</span>
                  </body.td>
                </body.tr>
              {{/unless}}
            {{/each}}
          </:tbody>
          <:tfoot as |foot|>
            {{#if this.hasRecords}}
              <foot.tr>
                <td colspan="100">
                  <Paginator
                    @totalItems={{this.totalItems}}
                    @pageSize={{PER_PAGE}}
                    @currentPage={{this.currentPage}}
                    @onChange={{this.onPageChange}}
                  />
                </td>
              </foot.tr>
            {{/if}}
          </:tfoot>
        </Table>
      </:body>
      <:footer>
        <div class="tio-row-between">
          <Button
            data-test-eligibility-export-csv-button
            @appearance="outlined"
            @isDisabled={{this.showExportPanel}}
            {{on "click" this.openExportPanel}}
          >
            {{t "eligibility_files.records.export.export_csv"}}
          </Button>
          <Button @appearance="outlined" {{on "click" this.handleClose}}>
            {{t "close"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
